import React, { useRef, useState, useCallback, useEffect } from "react";
import { Volume2, Loader2, Square } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ListenButton = ({ text, label = "Listen", className = "", testId = "listen-btn" }) => {
  const audioRef = useRef(null);
  const srcRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlaying(false);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const handleClick = useCallback(async () => {
    if (playing) { stop(); return; }
    if (!text || !text.trim()) return;
    setLoading(true);
    try {
      if (!srcRef.current) {
        const res = await fetch(`${API}/tts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text }),
        });
        if (!res.ok) throw new Error("tts failed");
        const data = await res.json();
        srcRef.current = data.audio;
      }
      const audio = new Audio(srcRef.current);
      audioRef.current = audio;
      audio.onended = () => setPlaying(false);
      audio.onerror = () => setPlaying(false);
      setPlaying(true);
      await audio.play();
    } catch {
      setPlaying(false);
    } finally {
      setLoading(false);
    }
  }, [playing, text, stop]);

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={testId}
      aria-label={playing ? "Stop audio" : `${label} — read aloud`}
      className={`inline-flex items-center gap-2 rounded-full border border-[#c6a15b]/50 bg-[#c6a15b]/10 px-4 py-2 text-sm font-semibold text-[#0b1f3a] hover:bg-[#c6a15b]/20 transition-colors ${className}`}
    >
      {loading ? (
        <><Loader2 className="h-4 w-4 animate-spin" /> Loading…</>
      ) : playing ? (
        <><Square className="h-4 w-4 fill-current" /> Stop</>
      ) : (
        <><Volume2 className="h-4 w-4 text-[#b08e46]" /> {label}</>
      )}
    </button>
  );
};

export default ListenButton;
